/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import {
  ISysQueGaugeFacade,
  SYSQUE_GAUGE_FACADE_TOKEN,
} from '../../../../../../data-access/src/lib/domain/facades-models/sysque-gauge-facade-interface';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { sysqueFamilyPartTypeList } from '../../../../../../data-access/src/lib/domain/data/sysque-family-part-type-list';
import { take } from 'rxjs';

@Component({
  selector: 'sysque-gauge-component',
  templateUrl: './sysque-gauge-component.html',
  styleUrl: './sysque-gauge-component.scss',
  standalone: false
})

/**
 * Represents the Sysque Gauge component.
 * This component is responsible for displaying and interacting with the gauge element.
 */
export class SysqueGaugeComponent implements OnInit {
  public state: SysqueElectricalState = new SysqueElectricalState();
  public selectedSystem: string;
  public rotationValue = 0;
  public diameter: string;
  public displayUnit: string;
  public width: number;
  public height: number;
  public familyPartTypeList = sysqueFamilyPartTypeList;

  constructor(@Inject(SYSQUE_GAUGE_FACADE_TOKEN) public sysqueGaugeFacade: ISysQueGaugeFacade) { }

  /**
   * Initializes the component after Angular initializes the data-bound input properties.
   * This lifecycle hook is called once after the first `ngOnChanges` hook is called.
   */
  public ngOnInit(): void {
    this.sysqueGaugeFacade.initializeSysQueGauge().pipe(take(1)).subscribe();;
    this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.SYSQUE_CONNECT_SYSTEM_NAME, '');
    this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, true);
  }

  /**
   * Updates the rotation value of the gauge component.
   * @param event - The event object containing the new rotation value.
   */
  public updateRotationValue(event: any): void {
    this.rotationValue = event.value;
    this.sysqueGaugeFacade.rotateSysQueConnect(this.rotationValue.toString()).pipe(take(1)).subscribe();
  }

  /**
   * Handles the click event on the gauge element.
   * Calculates the angle of the click position relative to the gauge element
   * and updates the rotation value accordingly.
   * @param event - The click event.
   */
  public onGaugeClick(event: MouseEvent): void {
    // Get the gauge element reference
    const gaugeElement = event.currentTarget as HTMLElement;

    // Calculate click position relative to the gauge element

    const rect = gaugeElement.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const relativeX = event.clientX - (rect.left + centerX);
    const relativeY = event.clientY - (rect.top + centerY);

    // Step 2: Calculate the angle from the click position
    let angle = Math.atan2(relativeY, relativeX) * (180 / Math.PI);

    // Adjust the angle to match the gauge's starting point
    angle += 180; // Assuming the gauge starts at -135 degrees
    if (angle < 0) {
      angle += 360; // Normalize the angle
    }

    // Clamp the value to the gauge's range
    this.rotationValue = Math.round(angle);
  }

  /**
   * Handles the input change event.
   * @param e - The input change event object.
   */
  public onInputChange(e: any): void {
    this.rotationValue = parseInt(e.event.target.value.slice(0, -1));
    if (this.rotationValue > 360) {
      this.rotationValue = 360;
    }
    this.sysqueGaugeFacade.rotateSysQueConnect(this.rotationValue.toString());
  }

  /**
   * Applies the gauge value to the system.
   * This method updates the system state, display unit, and fitting list based on the gauge value.
   * It also performs diameter and size checks, and enables/disables SysQue Connect for each fitting.
   * Finally, it updates the system state and closes the window.
   */
  public async applyGaugeValue(): Promise<void> {
    try {
      this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, true);
      await this.getDiameterAndSizeCheck();      
      const systemName = await this.sysqueGaugeFacade.getSelectedElementSystemName();
      if (systemName !== this.sysqueGaugeFacade.getSelectedSystemName()) {
       this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.SYSQUE_CONNECT_SYSTEM_NAME, systemName);      
      }
      else {
      this.state = this.sysqueGaugeFacade.getSystemState();
      this.displayUnit = this.state.systemList[0].unitType;

      this.state.fittingList.forEach((item) => {
        const sizeList: string[] = item.sizeRange.split(',');
        this.enableDisableSysQueConnect(item, sizeList);
      });
      this.sysqueGaugeFacade.updateDockableState(this.state);
      this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, false);
    }
      this.sysqueGaugeFacade.closeWindow();
    } catch (error) {
      console.error('An error occurred while applying the gauge value:', error);
    }
  }  

  /**
   * Converts the diameter to a string representation based on the display unit.
   * @param diameter - The diameter value to convert.
   * @param displayUnit - The display unit ('IMPERIAL' or 'METRIC').
   * @returns The converted diameter as a string.
   */
  private getSizeAsPerTemplate(diameter: number, displayUnit: string): string {
    if (displayUnit === 'IMPERIAL') {
      return Math.round(diameter * 12).toString();
    } else {
      return Math.round(diameter * 304.8).toString();
    }
  }

  /**
   * Enables or disables SysQue Connect for the given item and size list.
   *
   * @param item - The item for which to enable or disable SysQue Connect.
   * @param sizeList - The list of sizes for the item.
   * @returns A Promise that resolves when the operation is complete.
   */
  private async enableDisableSysQueConnect(item: Item, sizeList: string[]): Promise<void> {
    if (item.itemType === GlobalConstants.AddDefaultItem) {
      this.setEnableDisableForAddDefaultItem(item, sizeList);
    } else {
      this.setEnableDisableForElectricalTypeItem(item, sizeList);
    }
  }

  /**
   * Sets the enable/disable state for adding a default item.
   *
   * @param item - The item to be added.
   * @param sizeList - The list of sizes.
   */
  private setEnableDisableForAddDefaultItem(item: Item, sizeList: string[]): void {
    if (this.state.selectedCategory == GlobalConstants.Conduit) {
      this.enableDisableConduitForAddDefaultItem(item, this.displayUnit, parseFloat(this.diameter), sizeList);
    } else {
      this.enableDisableCableTrayForAddDefaultItem(item, sizeList);
    }
  }

  /**
   * Sets the enable/disable state for an electrical type item based on the selected category.
   * If the selected category is Conduit, it calls the enableDisableConduitElectricalTypeItem method.
   * Otherwise, it calls the enableDisableCableTrayElectricalTypeItem method.
   * @param item - The item to set the enable/disable state for.
   * @param sizeList - The list of sizes for the item.
   */
  private setEnableDisableForElectricalTypeItem(item: Item, sizeList: string[]): void {
    if (this.state.selectedCategory == GlobalConstants.Conduit) {
      this.enableDisableConduitElectricalTypeItem(item, this.displayUnit, parseFloat(this.diameter), sizeList);
    } else {
      this.enableDisableCableTrayElectricalTypeItem(item, sizeList);
    }
  }

  /**
   * Retrieves the diameter and size check for the SysQue gauge component.
   * If the selected category is not a tray item, it sets the diameter value.
   * Otherwise, it sets the width and height values based on the result.
   */
  private async getDiameterAndSizeCheck(): Promise<void> {
    const result = (await this.sysqueGaugeFacade.getSysQueConnectFittingVariables()) as string[];
    this.state = this.sysqueGaugeFacade.getSystemState();
    if (!this.isTrayItem(this.state.selectedCategory)) {
      this.diameter = result[0];      
      this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.GAUGE_DIAMETER_AND_SIZE, {
        conduit: this.getSizeAsPerTemplate(parseFloat(result[0]), this.state.systemList[0].unitType),
        width: '',
        height: '',
      });
    } else {
      this.sysqueGaugeFacade.updateLocalStorage(GlobalConstants.GAUGE_DIAMETER_AND_SIZE, {
        cableTray: '',
        width: result[0],
        height: result[1],
      });

      this.width = parseFloat(result[0]);
      this.height = parseFloat(result[1]);
    }
  }

  /**
   * Checks if the given shape is a tray item.
   * @param shape - The shape to check.
   * @returns `true` if the shape is a tray item, `false` otherwise.
   */
  private isTrayItem(shape: string): boolean {
    return (
      shape === GlobalConstants.WireWay ||
      shape === GlobalConstants.ChannelCableTray ||
      shape === GlobalConstants.LadderCableTray ||
      shape === GlobalConstants.WireMeshCableTray ||
      shape === GlobalConstants.BusWay
    );
  }

  /**
   * Verifies if the given size range is valid for the specified display unit, item, diameter, and size list.
   *
   * @param displayUnit - The display unit to check against.
   * @param item - The item to check against.
   * @param diameter - The diameter to check against.
   * @param sizeList - The list of sizes to check against.
   * @returns A boolean indicating if the size range is valid.
   */
  private verifySizeRange(displayUnit: string, item: Item, diameter: number, sizeList: string[]): boolean {
    return sizeList.some(
      (size) =>
        !size.includes('*') &&
        !size.toLowerCase().includes('x') &&
        size.trim().startsWith(this.getSizeAsPerTemplate(diameter, displayUnit)) &&
        this.familyPartTypeList.includes(item.fittingType)
    );
  }

  /**
   * Checks if the given item has a matching conduit size.
   *
   * @param item - The item to check.
   * @param displayUnit - The display unit.
   * @param diameter - The diameter.
   * @param sizeList - The list of sizes.
   * @returns A boolean indicating whether the item has a matching conduit size.
   */
  private hasMatchingConduitSize(item: Item, displayUnit: string, diameter: number, sizeList: string[]): boolean {
    return !sizeList.some(
      (size) =>
        !size.includes('*') &&
        size.trim().startsWith(this.getSizeAsPerTemplate(diameter, displayUnit) + 'x') &&
        this.familyPartTypeList.includes(item.fittingType)
    );
  }

  /**
   * Enables or disables the conduit for adding a default item.
   *
   * @param item - The item to be checked for enabling or disabling the conduit.
   * @param displayUnit - The display unit of the item.
   * @param diameter - The diameter of the item.
   * @param sizeList - The list of sizes for the item.
   */
  private enableDisableConduitForAddDefaultItem(item: Item, displayUnit: string, diameter: number, sizeList: string[]): void {
    if (this.state.selectedCategory === GlobalConstants.Conduit) {
      if (item.sizeRange !== null && item.sizeRange !== '') {
        this.checkAndEnableItemWithMatchingSize(item, displayUnit, diameter, sizeList);

        if (item.fittingType === GlobalConstants.MultiPort && item.offsetType !== null) {
          item.isDisabled = true;
        }
      }
    }
  }

  /**
   * Checks if the item has a matching conduit size and enables it if found.
   * @param item - The item to check and enable.
   * @param displayUnit - The display unit.
   * @param diameter - The diameter.
   * @param sizeList - The list of sizes.
   */
  private checkAndEnableItemWithMatchingSize(item: Item, displayUnit: string, diameter: number, sizeList: string[]): void {
    const hasMatchSize = this.hasMatchingConduitSize(item, displayUnit, diameter, sizeList);
    if (hasMatchSize) {
      this.enableItemWithMatchingSize(displayUnit, item, diameter, sizeList);
    } else {
      item.isDisabled = false;
    }
  }

  /**
   * Enables or disables an item based on the matching size.
   * @param displayUnit - The display unit.
   * @param item - The item to enable or disable.
   * @param diameter - The diameter value.
   * @param sizeList - The list of sizes.
   */
  private enableItemWithMatchingSize(displayUnit: string, item: Item, diameter: number, sizeList: string[]): void {
    if (this.verifySizeRange(displayUnit, item, diameter, sizeList)) {
      item.isDisabled = false;
    } else {
      item.isDisabled = true;
    }
  }

  /**
   * Enables or disables the cable tray for adding a default item.
   * @param item - The item to enable or disable the cable tray for.
   * @param sizeList - The list of sizes to check against for matching cable tray size.
   */
  private enableDisableCableTrayForAddDefaultItem(item: Item, sizeList: string[]): void {
    if (item.sizeRange !== null && item.sizeRange !== '') {
      if (this.hasMatchingCableTraySize(item, sizeList)) {
        item.isDisabled = false;
      } else {
        this.verifySizeRangeWidthHeight(item, sizeList);
      }
    }
  }

  /**
   * Verifies if the provided item has a matching size within the given size list.
   * Updates the `isDisabled` property of the item accordingly.
   *
   * @param item - The item to be checked for size matching.
   * @param sizeList - The list of sizes to compare against.
   */
  private verifySizeRangeWidthHeight(item: Item, sizeList: string[]): void {
    const hasMatchingSize = sizeList.some(
      (size) =>
        !size.includes('*') &&
        size.trim().startsWith(this.width + 'x' + this.height) &&
        this.familyPartTypeList.includes(item.fittingType)
    );
    item.isDisabled = !hasMatchingSize;
  }

  /**
   * Checks if the given item has a matching cable tray size in the provided size list.
   * @param item - The item to check.
   * @param sizeList - The list of cable tray sizes to compare against.
   * @returns A boolean indicating whether a matching cable tray size was found.
   */
  private hasMatchingCableTraySize(item: Item, sizeList: string[]): boolean {
    return sizeList.some(
      (size) =>
        !size.includes('*') &&
        size.startsWith(this.width + 'x' + this.height + 'x') &&
        this.familyPartTypeList.includes(item.fittingType)
    );
  }

  /**
   * Enables or disables a conduit electrical type item based on the provided parameters.
   * @param item - The item to enable or disable.
   * @param displayUnit - The display unit for the conduit.
   * @param diameter - The diameter of the conduit.
   * @param sizeList - The list of sizes for the conduit.
   */
  private enableDisableConduitElectricalTypeItem(
    item: Item,
    displayUnit: string,
    diameter: number,
    sizeList: string[]
  ): void {
    if (
      sizeList.some(
        (size) => !size.includes('*') && size.startsWith(this.getSizeAsPerTemplate(diameter, displayUnit).toString())
      )
    ) {
      item.isDisabled = false;
    } else {
      item.isDisabled = true;
    }
  }

  /**
   * Enables or disables the cable tray electrical type item based on the size list.
   * @param item - The item to enable or disable.
   * @param sizeList - The list of sizes to check against.
   */
  private enableDisableCableTrayElectricalTypeItem(item: Item, sizeList: string[]): void {
    if (sizeList.some((size) => !size.includes('*') && size.startsWith(this.width + 'x' + this.height))) {
      item.isDisabled = false;
    } else {
      item.isDisabled = true;
    }
  }
}